// Catalog.ID Client — verifying the ledger's signed rows
// ============================================================================
// apps/bitcash/docs/plans/bitcash-v2.md M12, §4.16, D59, §4.21.4: every row the ledger serves is
// signed — Ed25519 over `"bitcash-row-v2\0" ‖ C`, C the 18 length-prefixed
// fields (`operatorFee` and `earnedUnits` withdrawn, `country` and `payerKind`
// appended) — under the key `/api/keys` publishes for the row's kid and era.
// The CID client checks the rows behind what it shows: a balance appears only
// when the latest signed row of the wallet carries the same `balanceAfter`, and
// a release's bits only when the wallet's signed `lock-release` row says so.
//
// The key is taken from the ledger's own `/api/keys` on the network the node
// names; pinning it to the `bitcash_administrator_certificate` of the trust-root
// ledger is the wallets' (M13) and the commercial track's (M16) step.

import { ed25519 } from '@noble/curves/ed25519.js';
import {
    canonicalRowString, fromBase64url, rowRuleProblem, rowSigningInput, type NetworkId, type SignedRowFields,
} from '@catalog-bitcash-code/format';
import type { BitcashWorker, LedgerKeys, LedgerRowJson } from './bitcash-worker';
import { unitsToBit } from './bitcash-units';
import { viewCodeOf } from '../crypto/bitcash';

/** Why `row` is not a row this client may believe, or null when it verifies. */
export function verifyRow(
    row: LedgerRowJson, keys: LedgerKeys, expected: { networkId: NetworkId; walletRef: string },
): string | null {
    if (row.version !== 'v2') return `the row is of version ${row.version}`;
    if (row.network !== expected.networkId || keys.net !== expected.networkId) return `the row is for network ${row.network}`;
    if (row.walletRef !== expected.walletRef) return 'the row is of another wallet';
    // A key's era is a JSON number on /api/keys, a row's a decimal string: compared as text.
    const key = keys.keys.find(k => k.kid === row.kid && String(k.era) === row.era);
    if (key === undefined) return `the ledger publishes no key ${row.kid} for era ${row.era}`;
    const { sig, ...fields } = row;
    let C: Uint8Array;
    try {
        C = canonicalRowString(fields as SignedRowFields);
    } catch (err) {
        return `the row is malformed: ${err instanceof Error ? err.message : String(err)}`;
    }
    const rule = rowRuleProblem(fields as SignedRowFields);
    if (rule !== null) return `the row breaks the registry: ${rule}`;
    try {
        if (!ed25519.verify(fromBase64url(sig), rowSigningInput(C), fromBase64url(key.publicKey))) return 'the row\'s signature does not verify';
    } catch {
        return 'the row\'s signature does not verify';
    }
    return null;
}

/**
 * The wallet's balance in BIT, read with its VIEW code and confirmed by its
 * latest signed row — the row whose `seq` is the wallet's `lastSeq`. Throws when
 * the two disagree or the row does not verify: a pane says "could not be read"
 * rather than show a figure nobody signed. A wallet with no row yet holds
 * nothing, and any other figure for it is refused.
 */
export async function verifiedBalance(
    ledger: BitcashWorker, walletKey: string, networkId: NetworkId, walletRef: string,
): Promise<string> {
    const view = viewCodeOf(walletKey, networkId);
    const info = await ledger.walletInfo(view);
    if (info.lastSeq === '0') {
        if (info.balance !== '0') throw new Error('The ledger\'s answer for this wallet did not verify: it names a balance but no signed row');
        return '0';
    }
    const history = await ledger.walletHistory(view, String(BigInt(info.lastSeq) - 1n), 1);
    const row = history.rows[0];
    if (row === undefined) throw new Error('The ledger\'s answer for this wallet did not verify: its latest row is missing');
    if (row.seq !== info.lastSeq) throw new Error('The ledger\'s answer for this wallet did not verify: the row it served is not the latest');
    const problem = verifyRow(row, await ledger.keys(), { networkId, walletRef });
    if (problem !== null) throw new Error(`The ledger's answer for this wallet did not verify: ${problem}`);
    if (row.balanceAfter !== info.balance) {
        throw new Error('The ledger\'s answer for this wallet did not verify: its balance is not the one its latest signed row carries');
    }
    return unitsToBit(info.balance);
}

/** How far into a fresh wallet's history its release row is looked for. */
const RELEASE_ROW_WINDOW = 8;

/**
 * Confirms a lock release by its signed row (apps/bitcash/docs/plans/bitcash-v2.md M12): the
 * `lock-release` credit on the wallet `walletRef`, for exactly the `amount`
 * (units) the release answered, signed under `/api/keys`. The release answer
 * itself is unsigned; a print step reports the bits only once this resolves.
 * Throws otherwise.
 */
export async function verifiedRelease(
    ledger: BitcashWorker, walletKey: string, networkId: NetworkId, walletRef: string, amount: string,
): Promise<void> {
    const history = await ledger.walletHistory(viewCodeOf(walletKey, networkId), '0', RELEASE_ROW_WINDOW);
    const row = history.rows.find(r => r.type === 'lock-release' && r.walletRef === walletRef && r.amount === amount);
    if (row === undefined) throw new Error('The ledger\'s answer for this release did not verify: the wallet holds no signed release row for it');
    const problem = verifyRow(row, await ledger.keys(), { networkId, walletRef });
    if (problem !== null) throw new Error(`The ledger's answer for this release did not verify: ${problem}`);
}
